import { ObjectTools } from '../../../tools/object-tools';
import { SolIcon } from '@sol/icons';
import { SolApp, SolNavbarItem, SolRouteId } from '../../../models/sol-navigation';
import { SolMenuGroup } from '../../popover/sol-popover-menu-content/sol-popover-menu-content.state';
import { MenuTools } from '../../../tools/menu-tools';
import { StringTools } from '../../../tools/string-tools';
import { SolBadge } from '../../../models/core/sol-badge';

export interface SolNavbarSystemButton {
  id: SolRouteId;
  icon: SolIcon | undefined;
  badge: SolBadge;
}

export class SolNavbarState {
  private constructor(
    public readonly name: string,
    public readonly menu: SolNavbarItem[],
    public readonly system: SolNavbarItem[],
    public readonly account: SolNavbarItem[],
    public readonly activeRouteIds: SolRouteId[],
    /** True when the navbar is docked beside the content; false hides it off the left edge. */
    public readonly navbarVisible: boolean,
    /** Interaction state: a hidden navbar is slid open over the content. */
    public readonly drawerOpen: boolean,
    /** Whether sliding is animated. False across a mode switch, so that lands instantly. */
    public readonly drawerAnimated: boolean,
    public readonly expandedIds: SolRouteId[],
    public readonly app: SolApp | undefined,
    public readonly appsVisible: boolean,
    public readonly appsDropdownVisible: boolean,
    public readonly appHover: boolean,
    public readonly loading: boolean,
    public readonly initials: string,
    public readonly appDisplayName: string,
    public readonly appDisplayIcon: SolIcon | undefined,
    public readonly appMenu: SolMenuGroup[],
    public readonly accountMenu: SolMenuGroup[],
    public readonly systemMenus: Map<string, SolMenuGroup[]>,
    public readonly hostClasses: string,
    public readonly dockToggleIcon: SolIcon,
    public readonly itemCounts: Record<string, number>,
    public readonly systemButtons: SolNavbarSystemButton[],
  ) {}

  public static of(): SolNavbarState {
    return new SolNavbarState(
      '',
      [],
      [],
      [],
      [],
      true,
      false,
      false,
      [],
      undefined,
      false,
      false,
      false,
      true,
      '',
      '',
      undefined,
      [],
      [],
      new Map<string, SolMenuGroup[]>(),
      '',
      SolIcon.sidebar_left,
      {},
      [],
    ).recalculateHostClasses();
  }

  public withNavigation(
    app: SolApp | undefined,
    appMenu: SolMenuGroup[],
    appsVisible: boolean,
    appsDropdownVisible: boolean,
    appDisplayName: string,
    appDisplayIcon: SolIcon | undefined,
    menu: SolNavbarItem[],
    system: SolNavbarItem[],
    account: SolNavbarItem[],
    navbarVisible: boolean,
    drawerOpen: boolean,
    loading: boolean,
  ): SolNavbarState {
    return this.copy({
      app,
      appMenu,
      appsVisible,
      appsDropdownVisible,
      appDisplayName,
      appDisplayIcon,
      menu,
      system,
      account,
      navbarVisible,
      drawerOpen,
      loading,
      drawerAnimated: this.navbarVisible === navbarVisible && this.drawerAnimated,
    })
      .recalculateHostClasses()
      .recalculateDockToggleIcon()
      .recalculatePopoverMenus()
      .recalculateSystemButtons();
  }

  public withItemCounts(itemCounts: Record<string, number>): SolNavbarState {
    return this.copy({ itemCounts }).recalculateSystemButtons();
  }

  public withActiveNavbarItems(activeRouteIds: SolRouteId[]): SolNavbarState {
    return this.copy({ activeRouteIds, expandedIds: activeRouteIds });
  }

  public withName(name: string): SolNavbarState {
    const initials = StringTools.toInitials(name);
    return this.copy({ name, initials });
  }

  public withExpandedIds(expandedIds: SolRouteId[]): SolNavbarState {
    return this.copy({ expandedIds });
  }

  public withAppHover(appHover: boolean): SolNavbarState {
    return this.copy({ appHover });
  }

  public withDrawerAnimated(drawerAnimated: boolean): SolNavbarState {
    if (this.drawerAnimated === drawerAnimated) {
      return this;
    }
    return this.copy({ drawerAnimated }).recalculateHostClasses();
  }

  /** The toggle points where it will send the navbar: away when docked, back when hidden. */
  private recalculateDockToggleIcon(): SolNavbarState {
    const dockToggleIcon = this.navbarVisible ? SolIcon.sidebar_left : SolIcon.sidebar_right;
    return this.copy({ dockToggleIcon });
  }

  private recalculateHostClasses(): SolNavbarState {
    let hostClasses = 'sol-navbar-container';
    if (!this.navbarVisible) {
      hostClasses += ' sol-navbar-container-hidden';
      if (this.drawerAnimated) {
        hostClasses += ' sol-navbar-container-hidden-animated';
      }
      if (this.drawerOpen) {
        hostClasses += ' sol-navbar-container-hidden-open';
      }
    }
    return this.copy({ hostClasses });
  }

  private recalculatePopoverMenus(): SolNavbarState {
    const accountMenu = MenuTools.navbarToMenu(this.account);
    const systemMenus = new Map<string, SolMenuGroup[]>();
    for (const systemItem of this.system) {
      if (systemItem.children.length > 0) {
        systemMenus.set(systemItem.id, MenuTools.navbarToMenu(systemItem.children));
      }
    }
    return this.copy({ accountMenu, systemMenus });
  }

  private recalculateSystemButtons(): SolNavbarState {
    const systemButtons = this.system
      .filter(item => item.hasAuthority)
      .map(item => {
        const count = this.itemCounts[item.id] ?? 0;
        return {
          id: item.id,
          icon: item.icon,
          badge: SolBadge.of()
            .withPlacement('inset')
            .withCount(count)
            .withVisible(count > 0),
        };
      });
    return this.copy({ systemButtons });
  }

  private copy(props: Partial<SolNavbarState>): SolNavbarState {
    return ObjectTools.copy<SolNavbarState>(this, props);
  }
}
